import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from './ChartFrame';
import { bucketByDay } from '../dateRange';
import { fmtNumber } from '../derive';

// Driver acquisition trend with stable WTD/MTD/YTD chips that do not change
// when the global date range changes. Chip styling mirrors the cockpit's
// segmented controls.
export default function CanvasDrivers({ drivers = [], range, driverWindows }) {
  const series = useMemo(
    () => bucketByDay(drivers, (d) => d.createdAt, range.start, range.end),
    [drivers, range]
  );
  const total = series.reduce((s, b) => s + b.count, 0);

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr] gap-3">
      <div className="grid grid-cols-4 gap-2">
        {[
          { id: 'wtd', label: 'WTD', value: driverWindows?.wtd, sub: 'Sun → today' },
          { id: 'mtd', label: 'MTD', value: driverWindows?.mtd, sub: 'Month to date' },
          { id: 'ytd', label: 'YTD', value: driverWindows?.ytd, sub: 'Year to date' },
          { id: 'total', label: 'All time', value: driverWindows?.total, sub: 'Total acquired' },
        ].map((w) => (
          <div key={w.id} className="rounded-lg border border-zinc-200 bg-white px-2 py-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{w.label}</p>
            <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-zinc-900">{fmtNumber(w.value || 0)}</p>
            <p className="text-[9.5px] text-zinc-500">{w.sub}</p>
          </div>
        ))}
      </div>
      <ChartFrame
        title="Driver acquisition"
        subtitle="Signups by day, in selected range"
        right={
          <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">
            {fmtNumber(total)} in range
          </span>
        }
        footer={
          <span>
            <code>driver.createdAt</code> is set once at signup by Mongoose <code>{'{ timestamps: true }'}</code> and is not
            mutated by profile or status changes — safe as the acquisition date.
          </span>
        }
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid stroke="#f4f4f5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} interval="preserveEnd" />
            <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartFrame>
    </div>
  );
}
